import type { Apis, SessionService } from '@efdigital/ll-addon-auth';
import { default as BaseCoachService } from '@efdigital/ll-addon-coaching/services/coach';
import type { CoachingSession, CoachProfile } from '@efdigital/ll-addon-data';
import type {
  CoachCoacheePii,
  CoachPostAttendanceMarkRequestBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';
import config from '@efdigital/ll-xceed-v2/config/environment';
import { service } from '@ember/service';
import type { Store } from '@warp-drive/core';

import { queryCoachProfileByUserId } from '../data/builders/coaching/coach-profiles.ts';
import {
  buildCoachCancelSessionRequest,
  buildCoachConfirmSessionRequest,
  buildCoachGetCoacheesRequest,
  buildCoachMarkAttendanceRequest,
  buildCoachRejectSessionRequest,
  parseCoachConfirmSessionResponse,
  parseCoachGetCoacheesResponse,
  parseCoachMarkAttendanceResponse,
  parseCoachRejectSessionResponse,
} from '../data/builders/coaching/sessions-as-coach.ts';

const apis: Apis = config['ll-addon-auth-apis'];

export type CronofyRedeemFromQueryResult =
  | 'none'
  | 'success'
  | 'already_linked'
  | 'failed';

export default class CoachService extends BaseCoachService {
  apis = apis;
  @service declare store: Store;
  @service declare session: SessionService;
  #cachedCoachProfile: { exists: boolean; profile?: CoachProfile } | null =
    null;
  #cachedCoachees: CoachCoacheePii[] | undefined = undefined;

  /**
   * Redeems a Cronofy OAuth `code` query param when present.
   * Always strips `code` from the URL after an attempt so refresh does not re-POST.
   */
  async validateCodeFromQueryParams(): Promise<CronofyRedeemFromQueryResult> {
    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    if (!code) {
      return 'none';
    }

    url.searchParams.delete('code');
    const cleaned = `${url.pathname}${url.search}${url.hash}`;

    try {
      await this.validateCode(code);
      history.replaceState({}, '', cleaned);
      return 'success';
    } catch (error) {
      history.replaceState({}, '', cleaned);
      console.error('Failed to validate Cronofy code from query params', error);
      // Match addon `CronofyAccountAlreadyLinkedError` by name so this app
      // does not need a hard import of a newly published export.
      if (
        error instanceof Error &&
        error.name === 'CronofyAccountAlreadyLinkedError'
      ) {
        return 'already_linked';
      }
      return 'failed';
    }
  }

  async #ensureCachedCoachProfile() {
    if (this.#cachedCoachProfile) {
      return this.#cachedCoachProfile;
    }

    const response = await this.store.request(
      queryCoachProfileByUserId(this.session.cached.userId!, 'published')
    );
    const hasProfile = response.content.data.length > 0;
    this.#cachedCoachProfile = {
      exists: hasProfile,
      profile: hasProfile ? response.content.data[0] : undefined,
    };
    return this.#cachedCoachProfile;
  }

  async isCoach() {
    return (await this.#ensureCachedCoachProfile()).exists;
  }

  async getPublishedProfile() {
    const profile = (await this.#ensureCachedCoachProfile()).profile;
    if (!profile) {
      throw new Error('Coach profile not found');
    }
    return profile;
  }

  async #ensureCachedCoachees() {
    if (this.#cachedCoachees) {
      return this.#cachedCoachees;
    }

    const response = await fetch(
      await buildCoachGetCoacheesRequest(this.apis, this.session)
    );
    if (!response.ok) {
      throw new Error('Failed to fetch coachees');
    }
    const data = await parseCoachGetCoacheesResponse(response);
    return data.coachees;
  }

  async getCoachees(): Promise<CoachCoacheePii[]> {
    return await this.#ensureCachedCoachees();
  }

  async lookupCoachee(userId: string): Promise<CoachCoacheePii | undefined> {
    if (this.#cachedCoachees) {
      const alreadyLoaded = this.#cachedCoachees.find(
        (coachee) => coachee.userId === userId
      );
      if (alreadyLoaded) {
        return alreadyLoaded;
      }
    }
    return (await this.#ensureCachedCoachees()).find(
      (coachee) => coachee.userId === userId
    );
  }

  async cancelSession(coachingSession: CoachingSession, cancelReason: string) {
    const query = await buildCoachCancelSessionRequest(
      this.apis,
      this.session,
      coachingSession,
      { cancelReason }
    );

    const response = await fetch(query);
    if (!response.ok) {
      throw new Error('Failed to cancel session');
    }
  }

  async confirmSession(coachingSession: CoachingSession) {
    const query = await buildCoachConfirmSessionRequest(
      this.apis,
      this.session,
      coachingSession
    );

    const response = await fetch(query);
    const data = await parseCoachConfirmSessionResponse(response);
    coachingSession.status = data.session.status;
  }

  async rejectSession(coachingSession: CoachingSession, cancelReason: string) {
    const query = await buildCoachRejectSessionRequest(
      this.apis,
      this.session,
      coachingSession,
      { cancelReason }
    );

    const response = await fetch(query);
    const data = await parseCoachRejectSessionResponse(response);
    coachingSession.status = data.session.status;
  }

  async markAttendance(
    coachingSession: CoachingSession,
    body: CoachPostAttendanceMarkRequestBody
  ) {
    const query = await buildCoachMarkAttendanceRequest(
      this.apis,
      this.session,
      coachingSession,
      body
    );

    const response = await fetch(query);
    if (!response.ok) {
      throw new Error('Failed to mark attendance');
    }

    return await parseCoachMarkAttendanceResponse(response);
  }
}
